import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Doughnut, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Title,
} from 'chart.js';
import AdminLayout from '../layout/AdminLayout';
import { useAuth } from '../../context/AuthContext';
import { Statistics, AdminReport } from "../../services/api";

ChartJS.register(ArcElement, LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Title);

const AdminRepo = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data: stats = {}, isLoading: statsLoading, error: statsError } = useQuery({
    queryKey: ['adminStatistics'],
    queryFn: Statistics,
    refetchInterval: 30000,
  });

  const { data: report = {}, isLoading: reportLoading, error: reportError } = useQuery({
    queryKey: ['adminReport'],
    queryFn: AdminReport,
    refetchInterval: 30000,
  });

  const totalRevenue = report.sum_total_amount || report.sum_amount || stats.total_revenue || 0;
  const totalInvoices = report.total_invoices || 0;
  const totalTransactions = report.total_invoices || stats.total_transactions || 0;

  const METHOD_LABELS = {
    remita: 'QuickPay',
    wallet: 'Wallet Pay',
    flutterwave: 'Flutterwave',
    monnify: 'Monnify',
    bank_transfer: 'Bank Transfer',
  };
  const METHOD_COLORS = ['#22C55E', '#3B82F6', '#F59E0B', '#8B5CF6', '#EF4444'];

  const paymentMethodData = useMemo(() => {
    const methods = report.method_counts || {};
    const labels = [];
    const values = [];
    const bgColors = [];
    const colorList = METHOD_COLORS;
    let colorIdx = 0;

    Object.entries(methods).forEach(([method, info]) => {
      if ((info.count || 0) > 0) {
        labels.push(METHOD_LABELS[method] || method.charAt(0).toUpperCase() + method.slice(1));
        values.push(info.count);
        bgColors.push(colorList[colorIdx % colorList.length]);
        colorIdx++;
      }
    });

    return {
      labels,
      datasets: [{ data: values, backgroundColor: bgColors, borderWidth: 0 }],
    };
  }, [report]);

  const statusBreakdown = useMemo(() => {
    const counts = report.status_counts || {};
    return {
      pending: counts.pending?.count || 0,
      completed: counts.completed?.count || 0,
      failed: counts.failed?.count || 0,
      refunded: counts.refunded?.count || 0,
      pendingAmount: Number(counts.pending?.sum_amount || 0),
      completedAmount: Number(counts.completed?.sum_amount || 0),
      failedAmount: Number(counts.failed?.sum_amount || 0),
      refundedAmount: Number(counts.refunded?.sum_amount || 0),
    };
  }, [report]);

  const methodBreakdown = useMemo(() => {
    const methods = report.method_counts || {};
    return {
      walletCount: methods.wallet?.count || 0,
      walletAmount: Number(methods.wallet?.sum_amount || 0),
      quickpayCount: methods.remita?.count || 0,
      quickpayAmount: Number(methods.remita?.sum_amount || 0),
      flutterwaveCount: methods.flutterwave?.count || 0,
      flutterwaveAmount: Number(methods.flutterwave?.sum_amount || 0),
      monnifyCount: methods.monnify?.count || 0,
      monnifyAmount: Number(methods.monnify?.sum_amount || 0),
      bankTransferCount: methods.bank_transfer?.count || 0,
      bankTransferAmount: Number(methods.bank_transfer?.sum_amount || 0),
    };
  }, [report]);

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' },
      tooltip: { enabled: true },
    },
  };

  const isLoading = statsLoading || reportLoading;
  const hasError = statsError || reportError;

  if (user.role !== 'ADMIN') {
    return (
      <div className="flex items-center justify-center min-h-screen text-xl text-red-600">
        Access Denied
      </div>
    );
  }

  return (
    <AdminLayout>
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Overview</h2>
        <p className="text-gray-600 mt-1">Real-time insight into platform performance</p>
      </div>

      {hasError ? (
        <div className="bg-red-50 border border-red-200 text-red-700 p-6 rounded-lg">
          Error loading dashboard data. Please try again later.
        </div>
      ) : isLoading ? (
        <div className="text-center py-20 text-gray-500 text-lg">
          Loading dashboard...
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-6 mb-10">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Total Revenue</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                ₦{Number(totalRevenue).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Total Invoices</p>
              <p className="text-3xl font-bold text-gray-900 mt-2">
                {totalInvoices.toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Completed</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                {statusBreakdown.completed}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(statusBreakdown.completedAmount).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Failed</p>
              <p className="text-3xl font-bold text-red-700 mt-2">
                {statusBreakdown.failed}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(statusBreakdown.failedAmount).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">Pending</p>
              <p className="text-3xl font-bold text-yellow-700 mt-2">
                {statusBreakdown.pending}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{Number(statusBreakdown.pendingAmount).toLocaleString()}
              </p>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <p className="text-sm text-gray-600 font-medium">QuickPay Payments</p>
              <p className="text-3xl font-bold text-green-700 mt-2">
                {methodBreakdown.quickpayCount}
              </p>
              <p className="text-sm text-gray-500 mt-1">
                ₦{methodBreakdown.quickpayAmount.toLocaleString()}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-5">Payment Method Distribution</h3>
              <div className="h-72">
                <Doughnut data={paymentMethodData} options={chartOptions} />
              </div>
            </div>

            <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
              <h3 className="text-lg font-semibold mb-5">Invoice Status Breakdown</h3>
              <div className="h-72 flex flex-col justify-center gap-4 px-4">
                {[
                  { label: 'Completed', count: statusBreakdown.completed, amount: statusBreakdown.completedAmount, color: 'bg-green-500' },
                  { label: 'Pending', count: statusBreakdown.pending, amount: statusBreakdown.pendingAmount, color: 'bg-yellow-500' },
                  { label: 'Failed', count: statusBreakdown.failed, amount: statusBreakdown.failedAmount, color: 'bg-red-500' },
                  { label: 'Refunded', count: statusBreakdown.refunded, amount: statusBreakdown.refundedAmount, color: 'bg-purple-500' },
                ].map(({ label, count, amount, color }) => {
                  const total = statusBreakdown.completed + statusBreakdown.pending + statusBreakdown.failed + statusBreakdown.refunded || 1;
                  const pct = Math.round((count / total) * 100);
                  return (
                    <div key={label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700">{label}</span>
                        <span className="text-gray-500">{count} · ₦{Number(amount).toLocaleString()} ({pct}%)</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2.5">
                        <div className={`${color} h-2.5 rounded-full`} style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
            {[
              { label: 'QuickPay Payments',   count: methodBreakdown.quickpayCount,     amount: methodBreakdown.quickpayAmount,     color: 'text-green-700' },
              { label: 'Wallet Payments',      count: methodBreakdown.walletCount,       amount: methodBreakdown.walletAmount,       color: 'text-blue-700' },
              { label: 'Monnify Payments',     count: methodBreakdown.monnifyCount,      amount: methodBreakdown.monnifyAmount,      color: 'text-purple-700' },
              { label: 'Flutterwave Payments', count: methodBreakdown.flutterwaveCount,  amount: methodBreakdown.flutterwaveAmount,  color: 'text-indigo-700' },
            ].map(({ label, count, amount, color }) => (
              <div key={label} className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
                <p className="text-sm text-gray-600 font-medium">{label}</p>
                <p className={`text-2xl font-bold ${color} mt-2`}>{count.toLocaleString()}</p>
                <p className="text-sm text-gray-500 mt-1">₦{amount.toLocaleString()}</p>
              </div>
            ))}
          </div>
        </>
      )}
    </AdminLayout>
  );
};

export default AdminRepo;